import { aesGcmDecrypt, aesGcmEncrypt } from '@/crypto/aes-gcm'
import { bytesToUtf8, utf8ToBytes } from '@/crypto/encoding'
import { randomIvBytes } from '@/crypto/random'
import { type VaultCipherBlob, type VaultItemPayload } from '../types'

/**
 * Vault payload の暗号化 / 復号 (T-D-Vault-A、CLAUDE.md §2 ルール 2 厳守)。
 *
 * 設計判断:
 *   - 平文 payload は JSON.stringify した UTF-8 bytes として暗号化する。
 *     wire 上は `{ iv, ciphertext, authTag }` の 3-part base64 (= `VaultCipherBlob`)。
 *   - **IV は毎回 12 byte の crypto random で新規生成** する。同じ key + 同じ IV を
 *     2 回使うと AES-GCM の forgery 攻撃を許すため、絶対に再利用しない。
 *   - SK (`symmetricKey`) は呼出側 (`sessionStore.getState().symmetricKey`) から
 *     受け取る。本ファイルは zeroize 責務を **持たない** (SK は sessionStore が所有)。
 *   - `encryptVaultPayload` は payload を encode する手続きを caller 側で隠蔽し、
 *     失敗時 (JSON encode 失敗 / SK 未渡し等) は throw する。fall-through なし。
 *   - `decryptVaultPayload` は cipher の `iv` を base64 から復元し、key 違い /
 *     改ざんは AES-GCM の auth tag 検証で OperationError として throw される
 *     (Web Crypto 自身の挙動)。本ラッパは握りつぶさず再送出する。
 */

const VAULT_PAYLOAD_TYPES = ['login', 'note', 'card', 'identity'] as const

const isVaultItemPayload = (value: unknown): value is VaultItemPayload => {
  if (typeof value !== 'object' || value === null) return false
  const obj = value as Record<string, unknown>
  if (typeof obj.type !== 'string') return false
  if (!(VAULT_PAYLOAD_TYPES as readonly string[]).includes(obj.type)) return false
  if (typeof obj.name !== 'string') return false

  return true
}

/**
 * 後方互換正規化 (#1)。
 *
 * 旧 login payload は単一 `url` を持つ。`uris` が無く `url` が非空文字列なら
 * `uris: [{ uri: url }]` に展開する — 既存の暗号化済み vault アイテムを
 * 壊さず複数 URL モデルで読めるようにするため (CLAUDE.md §2 ルール 5)。
 * 保存時は `useVaultItemForm` が `uris` を書き出す。
 */
export const normalizeVaultPayload = (payload: VaultItemPayload): VaultItemPayload => {
  if (
    payload.type === 'login' &&
    (payload.uris === undefined || payload.uris.length === 0) &&
    typeof payload.url === 'string' &&
    payload.url.trim() !== ''
  ) {
    return { ...payload, uris: [{ uri: payload.url }] }
  }

  return payload
}

/**
 * 平文 payload を SK で AES-256-GCM 暗号化し、`VaultCipherBlob` を返す。
 *
 * @throws `RangeError` SK のバイト長が 32 ではない場合 (`aes-gcm.ts importGcmKey`)。
 */
export const encryptVaultPayload = async (
  symmetricKey: Uint8Array,
  payload: VaultItemPayload,
): Promise<VaultCipherBlob> => {
  const plaintext = utf8ToBytes(JSON.stringify(payload))
  const iv = randomIvBytes()

  return aesGcmEncrypt(symmetricKey, iv, plaintext)
}

/**
 * Wire 上の `VaultCipherBlob` を SK で復号し、`VaultItemPayload` を返す。
 *
 * @throws AES-GCM `OperationError` (auth tag 不一致 = 改ざん / 鍵違い / IV 違い)
 * @throws `SyntaxError` JSON parse 失敗 (= 平文 corruption)
 * @throws `Error` 復号結果が `VaultItemPayload` の形を満たさない場合
 */
export const decryptVaultPayload = async (
  symmetricKey: Uint8Array,
  cipher: VaultCipherBlob,
): Promise<VaultItemPayload> => {
  const plaintextBytes = await aesGcmDecrypt(symmetricKey, cipher)
  const decoded = bytesToUtf8(plaintextBytes)
  const parsed: unknown = JSON.parse(decoded)
  if (!isVaultItemPayload(parsed)) {
    throw new Error('decryptVaultPayload: decrypted JSON is not a valid VaultItemPayload')
  }

  // #1: 旧 single-url payload を複数 URL モデルへ正規化してから返す。
  return normalizeVaultPayload(parsed)
}
